import { Box, Button, IconButton, Tooltip, Typography, CircularProgress } from '@mui/material'
import { DeleteForever } from '@mui/icons-material'
import React, { useState, useContext } from 'react'
import { ReactComponent as Inactive } from '../../Assets/red_inactive_user.svg'
import { ReactComponent as Expired } from '../../Assets/red_expired_user.svg'
import { ReactComponent as Green } from '../../Assets/green_user.svg'
import { ReactComponent as Yellow } from '../../Assets/yellow_requested_user.svg'
import { BuildDropdown } from '../../Components/Inputs'
import { ConfirmActionDialog } from '../../Components/Dialogs'
import UserContext from '../../services/userService'
import WhoAmIContext from '../../services/whoami'

const UserCard = ({ batch, roleValues, statusValues, allowEditing, onCancelInvite, onCancelInviteError }) => {
  const service = useContext(UserContext)
  const { role: viewerRole } = useContext(WhoAmIContext)
  const [isEditing, setIsEditing] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false)
  const[filters, setFilters] = useState({
    status: batch.status,
    role: batch.role,
  })

  const filterOnChange = async (key, e) => {
    console.log('bleh', key, e)
    if(key.includes('status'))
      setFilters((oldState) => ({
          ...oldState,
          status: e,
      }))
    if(key.includes('role'))
      setFilters((oldState) => ({
          ...oldState,
          role: e,
      }))
  }

  const updateUser = async () => {
    setIsUpdating(true)
    const response = await service.updateCurrentUser(batch.userId, filters)
    setIsUpdating(false)
  }

  const reinviteUser = async () => {
    setIsUpdating(true)
    const response = await service.reinviteUser(batch.userId)
    setIsUpdating(false)
  }

  const cancelUserInvite = async () => {
    setIsConfirmingCancel(false)
    setIsCanceling(true)
    try {
      await service.cancelUserInvite(batch.userId)
      onCancelInvite?.(batch.userId)
    } catch (error) {
      console.error(error)
      onCancelInviteError?.()
    } finally {
      setIsCanceling(false)
    }
  }

  const handleClick = async () => {
    if(isEditing)
      await updateUser()
    setIsEditing(!isEditing)
  }

  return (
    <Box sx={{
      width: '100%',
      margin: '0 0 1rem 0',
      padding: '0.5rem',
      textAlign: 'start',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      borderBottom: '1px solid #9e9e9f20',
      backgroundColor: 'background.paper',
      '&:hover': { boxShadow: 3 },
    }}>
      <Box sx={{
        marginRight: '0.25rem',
        display: 'flex',
        flex: '3',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
      }}>
        {batch.status === 'INACTIVE'
          ? <Inactive width={'4rem'}/>
          : batch.status === 'INVITED'
            ? <Yellow width={'4rem'}/>
            : batch.status === 'EXPIRED'
              ? <Expired width={'4rem'}/>
              : <Green width={'4rem'}/>
          }
        <Typography noWrap sx={{
          width: '100%',
          textOverflow: 'ellipsis',
          marginLeft: '1.5rem',
          overflow: 'hidden',
          fontWeight: '600',
        }}>
          {batch.fullName.includes('undefined') ? 'Invited User' : batch.fullName}
        </Typography>
      </Box>

      <Typography component={'span'} noWrap sx={{
        marginRight: '0.25rem',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        flex: '2',
        fontWeight: '400',
        color: '#9e9e9f',
      }}>
        {isEditing 
        ? <BuildDropdown currentValue={filters.role} values={roleValues} setter={(e) => {filterOnChange('role', e)}} disableUnderline={true} isSmall={true}/>
        : batch.role === 'ADMIN' ? 'Super User' : 'User'
        }
      </Typography>

      <Typography noWrap sx={{
        marginRight: '0.25rem',
        textOverflow: 'ellipsis',
        flex: '2',
        overflow: 'hidden',
        fontWeight: '400',
        color: '#9e9e9f',
      }}>
        {batch.emailAddress}
      </Typography>

      <Typography component={'span'} noWrap sx={{
        marginRight: '0.25rem',
        textOverflow: 'ellipsis',
        textTransform: 'capitalize',
        flex: '2',
        overflow: 'hidden',
        fontWeight: '400',
        color: '#9e9e9f',
      }}>
        {isEditing 
        ? <BuildDropdown currentValue={filters.status} values={statusValues} setter={(e) => {filterOnChange('status', e)}} disableUnderline={true} isSmall={true}/>
        : batch.status === 'ACTIVE' 
          ? 'Active' 
          : batch.status === 'INACTIVE'
            ? 'Inactive'
            : batch.status === 'INVITED'
              ? 'Invited'
              : 'Expired'
        }
      </Typography>

      <Box sx={{
        marginRight: '0.25rem',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.5rem',
      }}>
      {allowEditing && <Button color='primary' variant={isEditing ? 'contained' : 'outlined'} sx={{
        padding: '0 1.5rem',
        textTransform: 'none',
      }} onClick={handleClick} disabled={isUpdating || isCanceling}>
        {isEditing
        ? isUpdating ? <CircularProgress sx={{ color: '#0071f4'}} padding={5} size={10} color={'inherit'}/>  : 'Save'
        : 'Edit'
        }
      </Button>}

      {batch.status ==='INVITED' && <Button color='primary' variant='outlined' sx={{
        padding: '0 1.5rem',
        textTransform: 'none',
      }} onClick={reinviteUser} disabled={isUpdating || isCanceling}>
        {isUpdating ? <CircularProgress size={16} color={'inherit'}/> : 'Reinvite'}
      </Button>}

      {viewerRole === 'ADMIN' && batch.status ==='INVITED' && <Tooltip title='Cancel invite'>
        <span>
          <IconButton
            aria-label='Cancel invite'
            color='error'
            size='small'
            onClick={() => setIsConfirmingCancel(true)}
            disabled={isUpdating || isCanceling}
          >
            {isCanceling ? <CircularProgress size={18} color='error'/> : <DeleteForever fontSize='small'/>}
          </IconButton>
        </span>
      </Tooltip>}
      </Box>

      <ConfirmActionDialog
        open={isConfirmingCancel}
        title='Cancel this invite?'
        description='This permanently withdraws the invite. The link already sent to this person will stop working, and this cannot be undone.'
        confirmLabel='Yes, withdraw'
        destructive
        onCancel={() => setIsConfirmingCancel(false)}
        onConfirm={cancelUserInvite}
      />
    </Box>
  )
}


export default UserCard
